<template>
  <Dashboard>
    <div class="max-w-7xl mx-auto flex flex-col gap-6">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <h1 class="text-3xl sm:text-4xl font-bold text-text-primary">{{ $t('play.matchmaking.title') }}</h1>
        <LanNavPills />
      </div>
      <div class="flex flex-col lg:flex-row lg:items-center gap-4">
        <div v-if="creator" class="flex-grow flex items-center gap-4 min-w-0">
          <span class="relative flex-shrink-0">
            <span class="block rounded-full p-[3px]" :style="{ background: channelLive ? '#E91916' : '#374151' }">
              <LanAvatar :src="creator.picture_url" size="w-16 h-16 border-4 border-gray-900" />
            </span>
            <span
              v-if="channelLive"
              class="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] font-bold text-white px-1.5 py-0.5 rounded"
              style="background:#E91916"
            >{{ $t('play.lan_party.live') }}</span>
          </span>
          <div class="min-w-0 flex flex-col gap-1">
            <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.lan_party.control_room_kicker') }}</p>
            <h3 class="text-text-primary text-3xl font-semibold leading-none truncate">{{ creator.name }}</h3>
            <p class="text-sm text-text-secondary flex flex-wrap items-center gap-x-2 gap-y-1">
              <a
                v-if="twitchName && channelUrl"
                :href="channelUrl"
                target="_blank"
                rel="noopener"
                class="inline-flex items-center gap-1 hover:underline"
                style="color:#BF94FF"
              ><TwitchIcon class="w-4 h-4" />{{ twitchName }}</a>
              <TwitchViewers v-if="channelLive" style="color:#BF94FF" :count="viewerCount" />
              <span v-if="room" class="tabular-nums">{{ $t('play.lan_party.on_air', { time: onAir, n: room.matches_played + 1 }) }}</span>
              <span v-else>{{ $t('play.lan_party.control_room_hint', { name: creator.name }) }}</span>
            </p>
          </div>
        </div>
        <div v-if="room" class="flex flex-wrap items-center gap-3">
          <button
            type="button"
            @click="copyLink"
            class="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-500"
            :title="joinUrl"
          >
            <LinkIcon class="w-4 h-4" aria-hidden="true" />{{ linkCopied ? $t('play.matchmaking.copied') : $t('play.lan_party.copy_join_link') }}
          </button>
          <button
            type="button"
            :disabled="busy"
            @click="confirmEnd = true"
            class="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-danger-700 text-danger-400 text-sm font-semibold hover:bg-danger-950 disabled:opacity-50"
          >
            <PowerIcon class="w-4 h-4" aria-hidden="true" />{{ $t('play.lan_party.end_night') }}
          </button>
        </div>
      </div>

      <p v-if="error" class="text-danger-400 text-sm">{{ error }}</p>

      <div
        v-if="room?.twitch && !room.twitch.connected"
        class="flex flex-col sm:flex-row sm:items-center gap-3 border-y border-gray-800 py-4"
      >
        <TwitchIcon class="w-5 h-5 flex-shrink-0" style="color:#BF94FF" />
        <p v-if="!room.twitch.found" class="flex-1 text-sm text-text-secondary">
          {{ $t('play.lan_party.twitch_not_found', { login: room.twitch.login, name: creator.name }) }}
        </p>
        <template v-else>
          <p class="flex-1 text-sm text-text-secondary">
            {{ $t('play.lan_party.twitch_connect_body', { login: room.twitch.login }) }}
            <span class="text-text-primary">{{ $t('play.lan_party.twitch_connect_hint', { login: room.twitch.login }) }}</span>
          </p>
          <button
            type="button"
            :disabled="busy"
            @click="connectTwitch"
            class="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style="background:#9146FF"
          ><TwitchIcon class="w-4 h-4" />{{ $t('play.lan_party.twitch_connect') }}</button>
        </template>
      </div>

      <div
        v-if="confirmEnd && room"
        class="rounded-xl border border-danger-800 bg-gray-900 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
      >
        <div class="flex-1">
          <p class="text-text-primary font-semibold">{{ $t('play.lan_party.end_confirm_title', room.members.length, { n: room.members.length }) }}</p>
          <p class="text-sm text-text-secondary">{{ $t('play.lan_party.end_confirm_body') }}</p>
        </div>
        <div class="flex gap-3">
          <button
            type="button"
            @click="confirmEnd = false"
            class="px-4 py-2 rounded-lg border border-gray-700 text-text-secondary text-sm hover:bg-gray-800"
          >{{ $t('play.lan_party.keep_running') }}</button>
          <button
            type="button"
            :disabled="busy"
            @click="endNight"
            class="px-4 py-2 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50"
          >{{ $t('play.lan_party.end_night') }}</button>
        </div>
      </div>

      <!-- No night open: start one -->
      <div
        v-if="creator && roomLoaded && !room"
        class="flex flex-col gap-6"
      >
        <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.lan_party.first_match') }}</p>
        <ModeMapRegion
          v-model:play-mode="form.playMode"
          v-model:map-name="form.mapName"
          v-model:region="form.region"
          :play-modes="playModes"
          :regions="regions"
        />
        <div class="flex flex-wrap items-center justify-end gap-4">
          <p class="text-sm text-text-secondary">{{ summary(form) }}</p>
          <button
            type="button"
            :disabled="!canStart || busy"
            @click="startNight"
            class="px-5 py-3 rounded-lg bg-primary-600 text-white text-base font-semibold hover:bg-primary-500 disabled:opacity-50 flex items-center gap-2"
          >
            <PlayCircleIcon class="w-5 h-5 opacity-60" aria-hidden="true" />
            {{ $t('play.lan_party.start_night') }}
          </button>
        </div>
      </div>

      <template v-if="room">
        <div>
          <div class="grid grid-cols-2 sm:grid-cols-5 gap-px rounded-lg overflow-hidden border border-gray-800 bg-gray-800">
            <div class="bg-gray-900 p-4 flex flex-col justify-center"><p class="text-2xl font-semibold tabular-nums text-text-primary">{{ room.members.length }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.stat_in_the_lan') }}</p></div>
            <div class="bg-gray-900 p-4 flex flex-col justify-center"><p class="text-2xl font-semibold tabular-nums text-secondary-300">{{ waiting.length }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.stat_waiting_for_you') }}</p></div>
            <div class="bg-gray-900 p-4 flex flex-col justify-center"><p class="text-2xl font-semibold tabular-nums text-text-primary">{{ quickPlay.length }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.stat_quick_play') }}</p></div>
            <div class="bg-gray-900 p-4 flex flex-col justify-center"><p class="text-2xl font-semibold tabular-nums text-text-primary">{{ room.matches_played }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.stat_matches_played') }}</p></div>
            <div class="bg-gray-900 p-4 flex flex-col justify-center"><p class="text-2xl font-semibold tabular-nums text-teal-400">+{{ room.sign_ups || 0 }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.stat_sign_ups') }}</p></div>
          </div>
        </div>

        <LanCurrentMatch
          v-if="currentMatch"
          :lobby="currentMatch"
          :manager-uuids="room.playing.map((p) => p.uuid)"
          :lan-party-uuid="room.uuid"
          @cancelled="loadRoom"
        />

        <div v-show="!currentMatch" class="flex flex-col gap-6 border-t border-gray-800 pt-6">
          <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.lan_party.next_match') }}</p>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="flex flex-col gap-3 h-full lg:order-last">
              <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">
                {{ $t('play.lan_party.lobby') }}
                <span class="normal-case tracking-normal font-normal text-text-primary tabular-nums">{{ $t('play.lan_party.lobby_count', { picked: picked.length, seats: room.seats }) }}</span>
              </p>
              <div class="grid grid-cols-3 gap-2">
                <span
                  v-for="player in room.playing"
                  :key="`creator-${player.member_id}`"
                  class="w-full h-[112px] rounded-lg border border-primary-500 bg-primary-950/40 flex flex-col items-center justify-center gap-1 relative"
                >
                  <LanAvatar :src="player.avatar" size="w-10 h-10 text-xs" />
                  <span class="text-sm text-text-primary truncate max-w-[7rem]">{{ player.username }}</span>
                  <span class="text-[10px] text-primary-300">{{ myMembership && player.member_id === myMembership.member_id ? $t('play.lan_party.you') : $t('play.lan_party.creator_side') }}</span>
                  <span v-if="myMembership && player.member_id === myMembership.member_id" class="absolute top-0.5 right-1 group">
                    <button
                      type="button"
                      :disabled="busy"
                      @click="togglePlaying"
                      class="text-text-secondary hover:text-text-primary text-xs px-1"
                      :aria-label="$t('play.lan_party.sit_out')"
                    >×</button>
                    <span
                      role="tooltip"
                      class="pointer-events-none absolute right-0 top-full mt-1 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] text-white shadow-lg opacity-0 transition-opacity group-hover:opacity-100 group-has-[:focus-visible]:opacity-100"
                    >{{ $t('play.lan_party.sit_out_hint') }}</span>
                  </span>
                </span>
                <button
                  v-if="!myMembership"
                  type="button"
                  :disabled="busy"
                  @click="togglePlaying"
                  class="w-full h-[112px] rounded-lg border border-dashed border-primary-700 flex flex-col items-center justify-center gap-1 text-xs font-semibold text-primary-300 hover:bg-primary-950/40 disabled:opacity-50"
                >
                  <PlusIcon class="w-5 h-5" aria-hidden="true" />
                  {{ $t('play.lan_party.count_me_in') }}
                </button>
                <span
                  v-for="member in picked"
                  :key="member.id"
                  class="w-full h-[112px] rounded-lg border border-secondary-400 bg-secondary-950/60 shadow-[0_0_24px_rgba(196,76,182,0.15)] flex flex-col items-center justify-center gap-1 relative"
                >
                  <LanAvatar :src="viewerAvatar(member)" size="w-10 h-10 text-xs" />
                  <span class="text-sm text-text-primary truncate max-w-[7rem]">{{ viewerName(member) }}</span>
                  <span class="text-[10px] text-secondary-300">{{ member.guest ? $t('play.lan_party.guest') : $t('play.lan_party.your_pick') }}</span>
                  <button
                    type="button"
                    :disabled="busy"
                    @click="pick(member, false)"
                    class="absolute top-0.5 right-1 text-text-secondary hover:text-text-primary text-xs"
                    :aria-label="$t('play.lan_party.unpick', { name: viewerName(member) })"
                  >×</button>
                </span>
                <span
                  v-for="n in emptySeats"
                  :key="`empty-${n}`"
                  class="w-full h-[112px] rounded-lg border border-dashed border-gray-700 flex items-center justify-center text-xs text-text-disabled"
                >{{ $t('play.lan_party.empty_seat') }}</span>
              </div>
            </div>

            <div
              class="flex flex-col gap-4 rounded-xl border border-secondary-700/60 p-5"
              style="background:linear-gradient(180deg,rgba(196,76,182,0.08),rgba(13,15,20,0.4) 40%)"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <p class="text-xs uppercase tracking-widest text-secondary-300 font-bold flex items-center gap-1.5"><SparklesIcon class="w-4 h-4" aria-hidden="true" />{{ $t('play.lan_party.waiting_for_you') }}</p>
                <div class="flex flex-row bg-gray-800 h-8 p-1 rounded-md" role="tablist">
                  <button
                    v-for="f in filters"
                    :key="f"
                    type="button"
                    class="px-2.5 text-xs rounded-md"
                    :class="filter === f ? 'bg-gray-700 text-text-primary' : 'text-text-secondary'"
                    @click="filter = f"
                  >
                    {{ f === 'all' ? $t('play.lan_party.filter_all', { n: queuedMembers.length }) : (f === 'subs' ? $t('play.lan_party.filter_subscribers') : $t('play.lan_party.filter_twitch')) }}
                  </button>
                </div>
              </div>
              <input
                v-model="search"
                type="search"
                :placeholder="$t('play.lan_party.search')"
                :aria-label="$t('play.lan_party.search')"
                class="text-text-primary placeholder:text-text-secondary bg-gray-800 block w-full px-3 py-2 border border-gray-700 rounded-md text-sm focus:outline-none focus:border-gray-400"
              />
              <div class="flex flex-col gap-1">
                <div
                  v-for="member in visibleMembers"
                  :key="member.id"
                  class="flex items-center gap-3 text-sm py-0.5"
                >
                  <LanAvatar :src="viewerAvatar(member)" size="w-7 h-7 text-[10px]" />
                  <span class="flex-1 min-w-0 flex items-center gap-1.5">
                    <span class="truncate text-text-primary" :title="member.username">{{ viewerName(member) }}</span>
                    <a
                      v-if="member.steam_id"
                      :href="`https://steamcommunity.com/profiles/${member.steam_id}`"
                      target="_blank"
                      rel="noopener"
                      class="inline-flex items-center flex-shrink-0 opacity-70 hover:opacity-100"
                      :title="$t('play.lan_party.steam_profile')"
                      :aria-label="$t('play.lan_party.steam_profile')"
                    ><img src="../assets/icons/steam.svg" alt="" class="block w-4 h-4" /></a>
                    <a
                      v-if="member.twitch"
                      :href="`https://www.twitch.tv/${member.twitch.login}`"
                      target="_blank"
                      rel="noopener"
                      class="inline-flex items-center flex-shrink-0"
                      :title="$t('play.lan_party.twitch_linked')"
                      :aria-label="$t('play.lan_party.twitch_linked')"
                      style="color:#BF94FF"
                    ><TwitchIcon class="block w-3.5 h-3.5" /></a>
                    <span
                      v-if="member.twitch_subscriber"
                      class="text-[10px] font-bold px-1 rounded"
                      :title="$t('play.lan_party.twitch_subscribed_to', { login: room.twitch?.login })"
                      style="background:#9146FF33;color:#BF94FF"
                    >{{ $t('play.lan_party.twitch_sub') }}</span>
                    <span
                      v-if="member.twitch_in_chat"
                      class="text-[10px] whitespace-nowrap"
                      style="color:#BF94FF"
                    >{{ $t('play.lan_party.twitch_in_chat') }}</span>
                    <span
                      v-if="member.guest"
                      class="text-[10px] text-text-secondary"
                    >{{ $t('play.lan_party.guest') }}</span>
                    <span
                      v-if="!member.wait_for_creator"
                      class="text-[10px] text-text-secondary"
                    >{{ $t('play.lan_party.playing_badge') }}</span>
                  </span>
                  <span
                    class="text-sm whitespace-nowrap"
                    :class="member.games_with_creator ? 'text-text-secondary' : 'text-secondary-300'"
                  >{{ $t('play.lan_party.games_with_you', { n: member.games_with_creator }) }}</span>
                  <span class="text-sm text-text-secondary tabular-nums w-12 text-right" :title="$t('play.lan_party.queued_for')">{{ waitedFor(member) }}</span>
                  <span
                    v-if="member.picked_at"
                    class="text-xs w-14 text-center text-secondary-300"
                  >{{ $t('play.lan_party.picked') }}</span>
                  <button
                    v-else
                    type="button"
                    :disabled="busy || emptySeats === 0"
                    @click="pick(member, true)"
                    class="text-xs font-semibold w-14 py-1.5 rounded-md"
                    :class="emptySeats === 0 ? 'bg-gray-800 text-text-disabled cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-500 text-white'"
                  >{{ $t('play.lan_party.pick') }}</button>
                </div>
                <p
                  v-if="visibleMembers.length === 0"
                  class="text-xs text-text-secondary"
                >{{ queuedMembers.length ? $t('play.lan_party.nobody_matches') : $t('play.lan_party.nobody_yet') }}</p>
              </div>
            </div>
          </div>
          <div class="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-3">
            <div v-if="showFill && twitchConnected" class="flex items-center gap-2 whitespace-nowrap sm:mr-auto">
              <span class="text-xs text-text-secondary">{{ $t('play.lan_party.fill_rest_with') }}</span>
              <div class="flex flex-row bg-gray-800 h-8 p-1 rounded-md" role="tablist">
                <button
                  type="button"
                  class="px-2.5 text-xs rounded-md"
                  :class="subscribersFirst ? 'text-text-secondary' : 'bg-gray-700 text-text-primary'"
                  @click="subscribersFirst = false"
                >{{ $t('play.lan_party.fill_fewest') }}</button>
                <button
                  type="button"
                  class="px-2.5 text-xs rounded-md"
                  :class="subscribersFirst ? 'bg-gray-700 text-text-primary' : 'text-text-secondary'"
                  @click="subscribersFirst = true"
                >{{ $t('play.lan_party.subscribers_first') }}</button>
              </div>
            </div>
            <PlaylistDropdown v-model="settings.playMode" :play-modes="playModes" compact class="sm:w-80" />
            <MapsDropdown
              class="sm:w-64"
              :model-value="settings.mapName ? [settings.mapName] : []"
              :cs-maps="settingsMaps"
              single
              compact
              @update:model-value="(names) => { settings.mapName = names[0] || null; }"
            />
            <router-link
              v-if="activeLobby"
              :to="{ name: 'MatchmakingLobbyPage', params: { id: activeLobby.uuid } }"
              class="flex-shrink-0 whitespace-nowrap h-14 px-5 rounded-lg flex items-center bg-green-700 text-white text-base font-semibold hover:bg-green-600"
            >{{ $t('play.matchmaking.go_to_matchroom') }}</router-link>
            <button
              v-else-if="showFill"
              type="button"
              :disabled="busy || !canFill"
              @click="fill"
              class="flex-shrink-0 whitespace-nowrap h-14 px-5 rounded-lg bg-gray-800 text-text-primary text-base font-semibold hover:bg-gray-700 disabled:opacity-50"
            >{{ $t('play.lan_party.fill') }}</button>
            <button
              v-else
              type="button"
              :disabled="busy || !canStartMatch"
              @click="startMatch"
              class="flex-shrink-0 whitespace-nowrap h-14 px-5 rounded-lg bg-primary-600 text-white text-base font-semibold hover:bg-primary-500 disabled:opacity-50 flex items-center gap-2"
            >
              <PlayCircleIcon class="w-5 h-5 opacity-60" aria-hidden="true" />
              {{ $t('play.lan_party.start_match') }}
            </button>
          </div>

        </div>

        <LanLeaderboard :slug="creator.slug" :creator-name="creator.name" class="border-t border-gray-800 pt-6" />
      </template>
    </div>
  </Dashboard>
</template>

<script setup>
import { useHead } from '@unhead/vue';
import {
  ref, reactive, computed, watch, onMounted, onBeforeUnmount,
} from 'vue';
import axios from 'axios';
import moment from 'moment';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import {
  LinkIcon, PowerIcon, PlusIcon, SparklesIcon,
} from '@heroicons/vue/24/outline';
import { PlayCircleIcon } from '@heroicons/vue/24/solid';
import Dashboard from '../components/layouts/DashboardLayout.vue';
import LanAvatar from '../components/LanParty/LanAvatar.vue';
import TwitchIcon from '../components/LanParty/TwitchIcon.vue';
import TwitchViewers from '../components/LanParty/TwitchViewers.vue';
import ModeMapRegion from '../components/LanParty/ModeMapRegion.vue';
import LanNavPills from '../components/LanParty/LanNavPills.vue';
import LanCurrentMatch from '../components/LanParty/LanCurrentMatch.vue';
import PlaylistDropdown from '../components/Matchmaking/PlaylistDropdown.vue';
import MapsDropdown from '../components/Matchmaking/MapsDropdown.vue';
import LanLeaderboard from '../components/LanParty/LanLeaderboard.vue';
import { useQueueSearch } from '../composables/useMatchmakingQueue';
import {
  apiError, safeHttpUrl, startTwitchLink, twitchLogin,
} from '../utils/lan_party';

const POLL_MS = 10_000;

const { t } = useI18n();
const auth = useAuth();
const store = useStore();
const router = useRouter();
const route = useRoute();
const { activeLobby } = useQueueSearch();

// The managed brand comes from the parties payload; a URL for another brand sends you to the hub.
const creator = computed(() => {
  const mine = store.getters.matchmakingContext?.creator;
  return mine?.slug === route.params.slug ? mine : null;
});
useHead({ title: computed(() => creator.value && t('play.lan_party.room_page_title', { name: creator.value.name })) });
const channelUrl = computed(() => safeHttpUrl(creator.value?.channel_url));
const twitchName = computed(() => twitchLogin(creator.value?.channel_url));
const room = ref(null);
// The room refetches when the stream starts or stops; the parties payload only on load.
const channelLive = computed(() => room.value?.twitch?.live ?? !!creator.value?.twitch?.live);
const viewerCount = computed(
  () => room.value?.twitch?.viewer_count ?? creator.value?.twitch?.viewer_count,
);
const playModes = ref([]);
const regions = ref([]);
const error = ref('');
const busy = ref(false);
const confirmEnd = ref(false);
// The start form waits for the first room fetch, so an open night never flashes it.
const roomLoaded = ref(false);
const linkCopied = ref(false);
const search = ref('');
const filter = ref('all');
const subscribersFirst = ref(true);
const form = reactive({ playMode: null, mapName: null, region: null });
const settings = reactive({ playMode: null, mapName: null, region: null });
let pollTimer;
let unmounted = false;

const canStart = computed(() => !!form.playMode && !!form.mapName && !!form.region);
const joinUrl = computed(() => (creator.value ? `${window.location.origin}/join/${creator.value.slug}` : ''));
const onAir = computed(() => {
  if (!room.value) return '';
  const minutes = Math.max(0, moment().diff(moment(room.value.started_at), 'minutes'));
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
});
const members = computed(() => room.value?.members || []);
const queuedMembers = computed(() => members.value.filter((m) => m.queued));
const waiting = computed(
  () => queuedMembers.value.filter((m) => m.wait_for_creator && !m.picked_at),
);
const quickPlay = computed(() => queuedMembers.value.filter((m) => !m.wait_for_creator));
const picked = computed(() => queuedMembers.value
  .filter((m) => m.picked_at)
  .sort((a, b) => a.picked_at.localeCompare(b.picked_at)));
const currentMatch = computed(() => {
  const match = room.value?.current_match;
  if (!match) return null;
  const live = store.getters.matchmakingLobby;
  return live?.uuid === match.uuid ? { ...match, ...live, number: match.number } : match;
});
// Mirrors the backend: viewers fill the far team first, so each side needs someone.
const canStartMatch = computed(() => {
  const perTeam = settings.playMode?.players_per_team || 1;
  const creatorSide = room.value?.playing.length || 0;
  return picked.value.length > 0 && (creatorSide > 0 || picked.value.length > perTeam);
});
const settingsMaps = computed(() => [...(settings.playMode?.cs_maps || [])]
  .sort((a, b) => a.friendly_name.localeCompare(b.friendly_name)));
const emptySeats = computed(() => Math.max(0, (room.value?.seats || 0) - picked.value.length));
// Fill until every seat is taken or nobody queued is left to seat; then the match can start.
const canFill = computed(() => emptySeats.value > 0
  && queuedMembers.value.some((m) => !m.picked_at));
// Fill stays the main action until the lobby can start: an emptied seat brings it back.
const showFill = computed(() => canFill.value || !canStartMatch.value);
const myMembership = computed(
  () => room.value?.playing.find((p) => p.user_id === auth.user()?.id) || null,
);
const twitchConnected = computed(() => !!room.value?.twitch?.connected);
// Twitch filters only exist when the Creator has Twitch; Subscribers needs the connected channel.
const filters = computed(() => {
  if (twitchConnected.value) return ['all', 'subs', 'twitch'];
  return room.value?.twitch?.found ? ['all', 'twitch'] : ['all'];
});
watch(filters, (list) => {
  if (!list.includes(filter.value)) filter.value = 'all';
});
const visibleMembers = computed(() => {
  const needle = search.value.toLowerCase();
  const subsFirst = twitchConnected.value && subscribersFirst.value;
  return queuedMembers.value
    .filter((m) => filter.value === 'all'
      || (filter.value === 'subs' ? m.twitch_subscriber : m.twitch_linked))
    .filter((m) => !needle || [m.username, m.twitch?.display_name]
      .some((name) => name?.toLowerCase().includes(needle)))
    .sort((a, b) => (a.guest - b.guest)
      || (subsFirst ? (!!b.twitch_subscriber - !!a.twitch_subscriber) : 0)
      || (a.games_with_creator - b.games_with_creator)
      || (b.matches_tonight - a.matches_tonight))
    .slice(0, 12);
});

function summary(choice) {
  const { playMode, mapName, region } = choice;
  if (!playMode || !mapName || !region) return t('play.lan_party.pick_mode_map');
  const map = playMode.cs_maps.find((m) => m.name === mapName);
  return t('play.lan_party.next_match_summary', {
    mode: playMode.friendly_name, map: map?.friendly_name || mapName, region: region.name,
  });
}

function fail(err) {
  error.value = apiError(err, t('play.lan_party.network_error'));
}

function setRoom(data) {
  room.value = data;
  if (!data) return;
  settings.playMode = playModes.value.find((mode) => mode.id === data.play_mode?.id) || null;
  settings.mapName = data.cs_map?.name || null;
  settings.region = regions.value.find((region) => region.id === data.region.id) || data.region;
}

function loadRoom() {
  const uuid = creator.value?.lan_party_uuid;
  if (!uuid) {
    room.value = null;
    return Promise.resolve();
  }
  return axios.get(`/matchmaking/lan_parties/${uuid}`).then(({ data }) => {
    setRoom(data.status === 'open' ? data : null);
  }).catch(fail);
}

watch(() => store.getters.matchmakingLobby?.status, (status) => {
  if (['completed', 'cancelled'].includes(status)) loadRoom();
});

const now = ref(Date.now());
const clock = setInterval(() => { now.value = Date.now(); }, 1000);
onBeforeUnmount(() => clearInterval(clock));

// Time in the LAN queue, as m:ss: it restarts after every match they play.
function waitedFor(member) {
  if (!member.queued_at) return '';
  const seconds = Math.max(0, Math.floor((now.value - Date.parse(member.queued_at)) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function onRoomUpdate(event) {
  if (event.detail === creator.value?.lan_party_uuid) loadRoom();
}

function refreshContext() {
  return axios.get('/matchmaking/parties').then(({ data }) => {
    store.commit('setMatchmakingContext', data);
  }).catch(() => {});
}

function csMapId(playMode, mapName) {
  return playMode?.cs_maps.find((map) => map.name === mapName)?.id || null;
}

function startNight() {
  busy.value = true;
  error.value = '';
  axios.post('/matchmaking/lan_parties', {
    creator_slug: creator.value.slug,
    lan_party: {
      region_id: form.region.id,
      play_mode_id: form.playMode.id,
      cs_map_id: csMapId(form.playMode, form.mapName),
    },
  }).then(({ data }) => {
    setRoom(data);
    return refreshContext();
  }).catch(fail).finally(() => { busy.value = false; });
}

// Rejects on failure, so callers decide what a failed PATCH means for the page.
function patchRoom(body) {
  busy.value = true;
  error.value = '';
  return axios.put(`/matchmaking/lan_parties/${room.value.uuid}`, { lan_party: body })
    .then(({ data }) => setRoom(data))
    .finally(() => { busy.value = false; });
}

// Only a change the manager made: what the server just sent back is never echoed to it.
// A mode switch from the toolbar keeps the map only if the new mode has it.
watch(() => settings.playMode, (mode) => {
  if (!mode || settingsMaps.value.some((map) => map.name === settings.mapName)) return;
  settings.mapName = settingsMaps.value[0]?.name || null;
});

watch(() => [settings.playMode?.id, settings.mapName], ([modeId, mapName]) => {
  if (!room.value || !modeId) return;
  if (modeId === room.value.play_mode?.id && mapName === room.value.cs_map?.name) return;
  patchRoom({ play_mode_id: modeId, cs_map_id: csMapId(settings.playMode, mapName) }).catch(fail);
});

function endNight() {
  confirmEnd.value = false;
  patchRoom({ status: 'ended' }).then(() => {
    room.value = null;
    return refreshContext();
  }).catch(fail);
}

function pick(member, isPicked) {
  busy.value = true;
  axios.put(`/matchmaking/lan_parties/${room.value.uuid}/members/${member.id}`, { member: { picked: isPicked } })
    .then(loadRoom)
    .catch(fail)
    .finally(() => { busy.value = false; });
}

function fill() {
  busy.value = true;
  axios.post(`/matchmaking/lan_parties/${room.value.uuid}/fill`, { subscribers_first: subscribersFirst.value && twitchConnected.value })
    .then(({ data }) => setRoom(data))
    .catch(fail)
    .finally(() => { busy.value = false; });
}

function startMatch() {
  busy.value = true;
  error.value = '';
  axios.post(`/matchmaking/lan_parties/${room.value.uuid}/lobbies`).then(({ data }) => {
    store.commit('setMatchmakingLobby', data);
    router.push({ name: 'MatchmakingLobbyPage', params: { id: data.uuid } });
  }).catch(fail).finally(() => { busy.value = false; });
}

function togglePlaying() {
  busy.value = true;
  const request = myMembership.value
    ? axios.delete(`/matchmaking/lan_parties/${room.value.uuid}/members/${myMembership.value.member_id}`)
    : axios.post(`/matchmaking/lan_parties/${room.value.uuid}/members`, { creator_side: true });
  request.then(loadRoom).catch(fail).finally(() => { busy.value = false; });
}

// The Creator knows chat by Twitch name and picture; the Refrag ones come second.
function viewerName(member) {
  return member.twitch?.display_name || member.username;
}

function viewerAvatar(member) {
  return member.twitch?.avatar_url || member.avatar;
}

function connectTwitch() {
  busy.value = true;
  startTwitchLink(creator.value.slug).catch((e) => {
    busy.value = false;
    fail(e);
  });
}

function copyLink() {
  navigator.clipboard.writeText(joinUrl.value).then(() => {
    linkCopied.value = true;
    setTimeout(() => { linkCopied.value = false; }, 2000);
  });
}

watch(() => creator.value?.lan_party_uuid, () => { loadRoom(); });

onMounted(() => {
  Promise.allSettled([
    axios.get('/matchmaking/play_modes').then((response) => { playModes.value = response.data.play_modes; }),
    axios.get('/regions').then((response) => { regions.value = response.data.regions; }),
    refreshContext(),
  ]).then(() => {
    if (!creator.value) return router.replace({ name: 'MatchmakingLanPage' });
    [form.playMode] = playModes.value;
    form.mapName = [...(form.playMode?.cs_maps || [])]
      .sort((a, b) => a.friendly_name.localeCompare(b.friendly_name))[0]?.name || null;
    [form.region] = regions.value;
    return loadRoom();
  }).then(() => {
    roomLoaded.value = true;
    if (!unmounted) pollTimer = setInterval(loadRoom, POLL_MS);
  });
});

window.addEventListener('lan-party-room-update', onRoomUpdate);

onBeforeUnmount(() => {
  unmounted = true;
  window.removeEventListener('lan-party-room-update', onRoomUpdate);
  clearInterval(pollTimer);
});
</script>
